// OPTIONS · scanner (Iron Condor plan Rev 2, Phase 1). READ-ONLY: finds and ranks short iron condors
// (BUY PE K1 < SELL PE K2 < SELL CE K3 < BUY CE K4) - or, with Strategy type = Box Spread in Settings,
// four-leg box spreads (BoxScanner) - across every NFO and MCX option underlying. Every figure comes from
// the worker (backend iron_condor_math / box_spread_math). No order can be created from the scanner.
import { BE_DISTANCE_NOTE, beDistanceBeyond, beDistanceText } from "@/lib/breakeven";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layers, Play, Radar, Search, Settings2, Square, Wrench } from "lucide-react";
import { toast } from "sonner";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import { pct, rupees } from "@/components/options/PairValuation";
import { age, StatusPill, usePoll } from "@/components/options/OptionsCommon";
import { CONTROL, FilterBar, FilterField, ScannerTable } from "@/components/options/ScannerTable";
import IcDetail, { marginText } from "@/components/iron_condor/IcDetail";
import IcSettings from "@/components/iron_condor/IcSettings";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import IcBuilder from "@/components/iron_condor/IcBuilder";
import IcTrades from "@/components/iron_condor/IcTrades";
import BoxScanner from "@/components/iron_condor/BoxScanner";
import BoxBuilder from "@/components/iron_condor/BoxBuilder";
import CoveragePanel from "@/components/iron_condor/CoveragePanel";
import { BOX_SPREAD_TRADING_ENABLED, IRON_CONDOR_TRADING_ENABLED } from "@/lib/optionsApi";
import BoxTrades from "@/components/iron_condor/BoxTrades";
import RowStateBadge from "@/components/RowStateBadge";

const TD = "px-1.5 py-1 text-right whitespace-nowrap mono";
const unitOr = (r, k) => (r.metrics[k] != null ? rupees(r.metrics[k]) : `${Number(r.metrics[`${k}_unit`]).toFixed(2)}/u`);
const be = (v) => (v == null ? "none" : Number(v).toFixed(1));

/** Expiry breakevens, then their distance from the row's spot (lib/breakeven). Stale quotes: greyed and labelled. */
export function BeCell({ r }) {
  const lo = r.metrics.breakeven_low, hi = r.metrics.breakeven_high;
  const dist = beDistanceText(r.spot, lo, hi);
  return (
    <span className="inline-flex flex-col items-end leading-tight" title={BE_DISTANCE_NOTE} data-testid={`ic-be-${r.id}`}>
      <span>{be(lo)} – {be(hi)}</span>
      <span className={`text-[10px] ${r.stale ? "text-[var(--term-muted)]" : beDistanceBeyond(r.spot, lo, hi) ? "text-[#FF3B30]" : "text-[var(--term-secondary)]"}`}
        data-testid={`ic-be-dist-${r.id}`}>{dist ? `${dist}${r.stale ? " · stale spot" : ""}` : "no spot"}</span>
    </span>
  );
}

export const IC_COLUMNS = [
  { id: "state", label: "State", align: "left", td: "px-1.5 py-1 text-left",
    cell: (r) => <RowStateBadge state={r.state} testId={`ic-state-${r.id}`} /> },
  { id: "underlying", label: "Underlying", align: "left", td: "px-1.5 py-1 text-left font-bold whitespace-nowrap",
    cell: (r) => <span>{r.underlying}<span className="ml-1 text-[9px] px-1 rounded-sm bg-[var(--term-s3)]">{r.exchange}</span></span> },
  { id: "expiry", label: "Expiry", td: TD, cell: (r) => r.expiry },
  { id: "spot", label: "Spot", td: TD, cell: (r) => (r.spot != null ? Number(r.spot).toLocaleString("en-IN") : "—") },
  { id: "strikes", label: "Strikes  BUY PE / SELL PE / SELL CE / BUY CE", align: "center", td: `${TD} text-center`,
    cell: (r) => {
      const [k1, k2, k3, k4] = r.metrics.strikes;
      return <span><span className="text-[#00C805]">{k1}</span> / <span className="text-[#FF3B30]">{k2}</span> / <span className="text-[#FF3B30]">{k3}</span> / <span className="text-[#00C805]">{k4}</span></span>;
    } },
  { id: "credit", label: "Net credit", sort: "credit", td: `${TD} text-[#00C805] font-bold`, cell: (r) => unitOr(r, "net_credit") },
  { id: "max_loss", label: "Max loss", sort: "max_loss", td: `${TD} text-[#FF3B30]`, cell: (r) => unitOr(r, "max_loss") },
  { id: "be", label: "Breakevens", td: TD, head: () => <span title={BE_DISTANCE_NOTE}>Breakevens (expiry)<br /><span className="font-normal normal-case">↓ / ↑ distance from spot</span></span>,
    cell: (r) => <BeCell r={r} /> },
  { id: "ror", label: "Return on risk", sort: "ror", td: TD, cell: (r) => pct(r.metrics.return_on_risk_pct) },
  { id: "rr", label: "Risk:Reward", td: TD, cell: (r) => r.metrics.risk_reward },
  { id: "widths", label: "Widths P/C", td: TD, cell: (r) => `${r.metrics.put_width}/${r.metrics.call_width}` },
  { id: "margin", label: "Broker margin", sort: "margin", td: TD, cell: (r) => marginText(r.capital) },
  { id: "rom", label: "Return on margin", sort: "rom", td: TD, cell: (r) => pct(r.capital?.return_on_margin_pct) },
  { id: "oi", label: "Min OI", td: TD, cell: (r) => (r.min_oi != null ? Number(r.min_oi).toLocaleString("en-IN") : "—") },
  { id: "age", label: "Quote age", sort: "age", td: TD,
    cell: (r) => <span className={r.stale ? "text-[#b38f00] font-bold" : ""} title={r.stale ? "Older than the maximum quote age: rescan before relying on it" : ""}>{age(r.quote_age_s)}{r.stale ? " STALE" : ""}</span> },
  { id: "source", label: "Source", td: TD, cell: (r) => r.source },
];

const SORTERS = {
  credit: (r) => r.metrics.net_credit ?? r.metrics.net_credit_unit,
  max_loss: (r) => -(r.metrics.max_loss ?? r.metrics.max_loss_unit),
  ror: (r) => r.metrics.return_on_risk_pct,
  margin: (r) => -(r.capital?.capital_required ?? Infinity),
  rom: (r) => r.capital?.return_on_margin_pct ?? -Infinity,
  age: (r) => -(r.quote_age_s ?? Infinity),
};

export function sortIcRows(rows, sort) {
  if (!sort || !SORTERS[sort.key]) return rows;
  const f = SORTERS[sort.key];
  const out = [...rows].sort((a, b) => f(b) - f(a));
  return sort.dir === "asc" ? out.reverse() : out;
}

export const IC_SECTIONS = {
  scanner: { label: "Scanner", icon: Radar, blurb: "Ranks iron condors or box spreads across every NFO and MCX option chain. Places no orders." },
  builder: { label: "Strategy Builder", icon: Wrench, blurb: "Price four legs from live quotes, check margin and payoff, then open a simulated trade." },
  trades: { label: "Trades", icon: Layers, blurb: "Box spread and iron condor trades: marks, exits and risk limits. Today every trade is simulated (shadow)." },
};
export function sectionOf(pathname) {
  const m = /\/options\/iron-condor\/(builder|trades)/.exec(pathname || "");
  return m ? m[1] : "scanner";
}

/** A titled block inside the Box & Iron Condor workspace. */
export function Section({ title, hint, right, children, testId }) {
  return (
    <section className="term-panel rounded-md border border-[var(--term-border)] p-3 space-y-3" data-testid={testId}>
      {(title || right) && <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0">
          {title && <h2 className="text-sm font-bold text-[var(--term-primary)]">{title}</h2>}
          {hint && <p className="text-[11px] text-[var(--term-muted)] mt-0.5">{hint}</p>}
        </div>
        {right && <div className="ml-auto flex items-center gap-2 flex-wrap">{right}</div>}
      </div>}
      {children}
    </section>
  );
}

function Segmented({ value, options, onChange, testId, label }) {
  return (
    <div className="inline-flex rounded-md border border-[var(--term-border-strong)] p-0.5 text-xs" role="radiogroup" aria-label={label} data-testid={testId}>
      {options.map(([k, l]) => (
        <button key={k} role="radio" aria-checked={value === k} onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
          className={`h-7 px-3 rounded-[5px] ${value === k ? "bg-[#2563EB] text-white font-bold" : "text-[var(--term-secondary)] hover:text-[var(--term-primary)]"}`}>{l}</button>))}
    </div>
  );
}

/** W4: the saved Iron Condor filters that can empty the list (a Rs 100 max loss removed every NFO condor on 26 Oct). */
export function icFilterText(st) {
  if (!st) return "";
  const out = [];
  if (st.max_loss != null) out.push(`max loss ${rupees(st.max_loss)} per condor`);
  if (st.min_net_credit != null) out.push(`min net credit ${rupees(st.min_net_credit)}`);
  if (st.min_return_on_risk_pct != null) out.push(`min return on risk ${st.min_return_on_risk_pct} %`);
  if (st.min_credit_to_width_pct != null) out.push(`min credit/width ${st.min_credit_to_width_pct} %`);
  return out.join(" · ");
}

export default function IronCondor() {
  const status = usePoll("/ic/status", { ms: 3000 });
  const running = !!status.data?.runtime?.running;
  const isBox = status.data?.settings?.strategy === "BOX";
  // one product, three routes: /options/iron-condor (scanner), /builder, /trades - each has its own sidebar entry
  const loc = useLocation();
  const navigate = useNavigate();
  const tab = sectionOf(loc.pathname);
  const setTab = (k) => navigate(k === "scanner" ? "/options/iron-condor" : `/options/iron-condor/${k}`);
  const [seed, setSeed] = useState(null);
  const [boxSeed, setBoxSeed] = useState(null);
  // sidebar entries pass ?kind= (Condor / Box Builder, Condor / Box Trade) and ?scan= (Condor / Box Scanner)
  const query = new URLSearchParams(loc.search);
  const kindParam = ["IRON_CONDOR", "BOX"].includes(query.get("kind")) ? query.get("kind") : null;
  const scanParam = ["IRON_CONDOR", "BOX"].includes(query.get("scan")) ? query.get("scan") : null;
  const [builderKind, setBuilderKind] = useState(kindParam);    // null = follow the scanner's strategy
  const kind = builderKind || (isBox ? "BOX" : "IRON_CONDOR");
  const [tradesKind, setTradesKind] = useState(kindParam);
  useEffect(() => { if (kindParam) { setBuilderKind(kindParam); setTradesKind(kindParam); } }, [kindParam]);
  const [switching, setSwitching] = useState(false);
  // the scanner has ONE saved strategy type; the Condor / Box Scanner entries never switch it silently
  const switchScanner = async (to) => {
    setSwitching(true);
    try { await optionsApi.put("/ic/settings", { strategy: to }); toast.success(`Scanner now searching ${to === "BOX" ? "box spreads" : "iron condors"}`); await status.reload(); }
    catch (e) { toast.error(optionsError(e)); }
    finally { setSwitching(false); }
  };
  const tKind = tradesKind || (isBox && BOX_SPREAD_TRADING_ENABLED ? "BOX" : IRON_CONDOR_TRADING_ENABLED ? "IRON_CONDOR" : "BOX");
  const anyTrading = IRON_CONDOR_TRADING_ENABLED || BOX_SPREAD_TRADING_ENABLED;
  const openBoxBuilder = (sd) => { setBoxSeed(sd); setBuilderKind("BOX"); setTab("builder"); };
  const [filters, setFilters] = useState({ exchange: "", underlying: "", source: "", fresh: false });
  const params = useMemo(() => ({ limit: 500, ...(filters.exchange ? { exchange: filters.exchange } : {}),
    ...(filters.underlying ? { underlying: filters.underlying } : {}), ...(filters.source ? { source: filters.source } : {}) }),
  [filters.exchange, filters.underlying, filters.source]);
  const cands = usePoll(running && tab === "scanner" && !isBox ? "/ic/candidates" : null, { ms: 2000, params });
  const [sort, setSort] = useState(null);
  const [open, setOpen] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState([]);
  const openBuilder = (r) => {
    setSeed({ exchange: r.exchange, underlying: r.underlying, expiry: r.expiry, lots: r.lots, strikes: r.metrics.strikes, at: Date.now() });
    setOpen(null); setBuilderKind("IRON_CONDOR"); setTab("builder");
  };
  const rows = useMemo(() => {
    const all = (cands.data?.rows || []).filter((r) => !filters.fresh || !r.stale);
    return sortIcRows(all, sort);
  }, [cands.data, sort, filters.fresh]);
  const health = status.data?.runtime?.health;

  const toggle = async () => {
    setBusy(true);
    try { await optionsApi.post(running ? "/ic/stop" : "/ic/start"); await status.reload(); }
    catch (e) { toast.error(optionsError(e)); }
    finally { setBusy(false); }
  };
  const onSort = (key) => setSort((s) => (s?.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  const cols = useMemo(() => [{
    id: "cmp", label: "Cmp", align: "center", td: "px-1.5 py-1 text-center",
    cell: (r, c) => <input type="checkbox" checked={c.compare.includes(r.id)} onClick={(e) => e.stopPropagation()}
      onChange={() => c.setCompare((x) => (x.includes(r.id) ? x.filter((i) => i !== r.id) : [...x, r.id].slice(-3)))} aria-label="Compare" />,
  }, ...IC_COLUMNS], []);
  const compared = (cands.data?.rows || []).filter((r) => compare.includes(r.id));

  if (status.error && !status.data) {
    return <div className="p-6 text-sm" data-testid="ic-unavailable"><div className="term-panel rounded-sm p-4">{status.error}</div></div>;
  }
  const sec = IC_SECTIONS[tab];
  const SecIcon = sec.icon;
  const btn = "h-8 px-3 rounded-md border border-[var(--term-border-strong)] inline-flex items-center gap-1.5 text-xs hover:bg-[var(--term-hover)]";
  const readOnly = (
    <span className="text-[10px] px-2 py-0.5 rounded-sm bg-[var(--term-s3)] font-bold" data-testid="scanner-readonly"
      title="The scanner itself places no orders. Trades are opened from the Strategy Builder and managed under Trades.">SCANNER READ-ONLY</span>);
  return (
    <div className="p-4 md:p-5 space-y-4 max-w-[1600px]" data-testid="iron-condor-page">
      {/* ── page header: product › section, the scanner's state, then section tabs ── */}
      <header className="space-y-2">
        <nav className="text-[11px] text-[var(--term-muted)] flex items-center gap-1" aria-label="Breadcrumb" data-testid="ic-breadcrumb">
          <span>Box &amp; Iron Condor</span><span aria-hidden>›</span><span className="text-[var(--term-secondary)] font-semibold">{sec.label}</span>
        </nav>
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-9 w-9 rounded-md bg-[#2563EB]/15 text-[#2563EB] flex items-center justify-center shrink-0"><SecIcon className="h-4 w-4" /></div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold leading-tight" data-testid="scanner-title">Box &amp; Iron Condor · {sec.label}</h1>
            <p className="text-xs text-[var(--term-muted)]">{sec.blurb}</p>
          </div>
          <div className="ml-auto flex gap-2 flex-wrap items-center">
            <span className="text-[10px] px-2 py-0.5 rounded-sm bg-[#2563EB]/15 text-[#2563EB] font-bold" title="Strategy type the scanner ranks (Settings)">
              SCANNING {isBox ? "BOX SPREADS" : "IRON CONDORS"}</span>
            {/* the scanner's FEED state - "LIVE" alone now means real money (lib/modes) */}
            <StatusPill status={running ? (status.data?.runtime?.feed_state || "RUNNING") : "STOPPED"}
              label={running ? `FEED ${status.data?.runtime?.feed_state || "RUNNING"}` : "SCANNER STOPPED"} />
            {tab === "scanner" && <>
              <button className={btn} onClick={() => setShowSettings(true)} aria-pressed={showSettings} data-testid="ic-settings-toggle"><Settings2 className="h-3.5 w-3.5" />Settings</button>
              <button className={btn} onClick={() => setShowCoverage((v) => !v)} aria-pressed={showCoverage} data-testid="coverage-toggle">Coverage</button>
            </>}
            <button disabled={busy} onClick={toggle} data-testid="ic-start-stop"
              className={`h-8 px-3 rounded-md text-xs font-bold inline-flex items-center gap-1 text-white disabled:opacity-40 ${running ? "bg-[#FF3B30]" : "bg-[#2563EB]"}`}>
              {running ? <><Square className="h-3.5 w-3.5" />Stop scanner</> : <><Play className="h-3.5 w-3.5" />Start scanner</>}</button>
          </div>
        </div>
        {status.data?.runtime?.error && <div className="text-xs text-[#FF3B30] rounded-md border border-[#FF3B30]/40 px-3 py-2">Scanner error: {status.data.runtime.error}</div>}
        {status.data?.runtime?.message && <div className="text-xs text-[#b38f00] rounded-md border border-[#b38f00]/40 px-3 py-2">{status.data.runtime.message}</div>}
        {/* section tabs mirror the three sidebar entries */}
        <div className="flex gap-1 border-b border-[var(--term-border)] overflow-x-auto" role="tablist">
          {Object.entries(IC_SECTIONS).filter(([k]) => k !== "trades" || anyTrading).map(([k, v]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`ic-tab-${k}`}
              className={`px-3 py-2 text-xs font-bold border-b-2 inline-flex items-center gap-1.5 whitespace-nowrap ${tab === k ? "border-[#2563EB] text-[var(--term-primary)]" : "border-transparent text-[var(--term-muted)] hover:text-[var(--term-primary)]"}`}>
              <v.icon className="h-3.5 w-3.5" />{v.label}</button>))}
        </div>
      </header>

      {tab === "scanner" && <>
        {scanParam && status.data?.settings && (scanParam === "BOX") !== isBox && (
          <div className="rounded-md border border-[#2563EB]/40 bg-[#2563EB]/5 px-3 py-2 text-xs flex flex-wrap items-center gap-2" data-testid="ic-scan-switch">
            <span>The scanner is set to <b>{isBox ? "Box Spread" : "Iron Condor"}</b>. Switching to {scanParam === "BOX" ? "Box Spread" : "Iron Condor"} saves the
              scanner's strategy type and restarts its sweep.</span>
            <button disabled={switching} onClick={() => switchScanner(scanParam)} data-testid="ic-scan-switch-btn"
              className="h-7 px-3 rounded-md bg-[#2563EB] text-white font-bold disabled:opacity-40">Switch scanner to {scanParam === "BOX" ? "Box Spread" : "Iron Condor"}</button>
          </div>)}
        {health && <div className="space-y-1" data-testid="ic-health">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[["Focus chains", health.focus], ["Universe", `${health.universe} chains`], ["Sweep", `${health.position}/${health.universe}`],
              ["Full pass", health.last_pass_s != null ? age(health.last_pass_s) : "—"]].map(([k, v]) => (
              <div key={k} className="term-panel rounded-md border border-[var(--term-border)] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-[var(--term-muted)]">{k}</div>
                <div className="text-sm font-bold mono">{v}</div>
              </div>))}
          </div>
          <div className="text-[11px] text-[var(--term-muted)] mono">
            focus {health.focus} · universe {health.universe} chains · sweep {health.position}/{health.universe} · budget {health.token_budget} tokens
            {health.over_budget_tokens ? ` · ${health.over_budget_tokens} over budget` : ""}</div>
        </div>}
        <Dialog open={showSettings && !!status.data?.settings} onOpenChange={(v) => { if (!v) setShowSettings(false); }}>
          <DialogContent className="max-w-4xl p-4 gap-3 bg-[var(--term-bg,var(--background))]" data-testid="ic-settings-dialog">
            <DialogHeader><DialogTitle className="text-sm">Scanner settings</DialogTitle>
              <DialogDescription className="text-[11px]">The five main filters; everything else is under Advanced Settings.</DialogDescription></DialogHeader>
            {showSettings && status.data?.settings && (
              <IcSettings settings={status.data.settings} onClose={() => setShowSettings(false)}
                onSaved={() => { setShowSettings(false); status.reload(); }} />)}
          </DialogContent>
        </Dialog>
        {showCoverage && <CoveragePanel running={running} onClose={() => setShowCoverage(false)} />}
        {isBox && <Section title="Box spread candidates" hint="Ranked from live quotes. Open one in the Strategy Builder to trade it."
          right={readOnly} testId="box-section">
          <BoxScanner running={running} settings={status.data?.settings} onOpenBuilder={openBoxBuilder} />
        </Section>}
        {!isBox && <Section title="Iron condor candidates" hint="Click a row for legs, payoff and margin; tick up to three to compare side by side."
          right={readOnly} testId="ic-section">
          <FilterBar testId="ic-filters">
            <FilterField label="Exchange"><select className={CONTROL} value={filters.exchange} data-testid="ic-filter-exchange"
              onChange={(e) => setFilters({ ...filters, exchange: e.target.value })}><option value="">All</option><option>NFO</option><option>MCX</option></select></FilterField>
            <FilterField label="Underlying"><span className="relative"><Search className="h-3 w-3 absolute left-2 top-2.5 text-[var(--term-muted)]" />
              <input className={`${CONTROL} pl-6 w-36`} value={filters.underlying} placeholder="e.g. NIFTY"
                onChange={(e) => setFilters({ ...filters, underlying: e.target.value.toUpperCase() })} data-testid="ic-filter-underlying" /></span></FilterField>
            <FilterField label="Source"><select className={CONTROL} value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
              <option value="">Focus + sweep</option><option value="FOCUS">Focus</option><option value="SWEEP">Sweep</option></select></FilterField>
            <FilterField label="Quotes"><label className="flex items-center gap-1 h-8"><input type="checkbox" checked={filters.fresh}
              onChange={(e) => setFilters({ ...filters, fresh: e.target.checked })} data-testid="ic-filter-fresh" />fresh only</label></FilterField>
            {(filters.exchange || filters.underlying || filters.source || filters.fresh) && <button className="h-8 px-2 text-xs text-[#2563EB] hover:underline"
              onClick={() => setFilters({ exchange: "", underlying: "", source: "", fresh: false })} data-testid="ic-filters-clear">Clear filters</button>}
            <span className="text-[11px] text-[var(--term-muted)] ml-auto">{rows.length} shown · {cands.data?.total ?? 0} candidates</span>
          </FilterBar>
          {cands.data?.coverage && <div className={`text-[11px] mono ${cands.data.coverage.chains_partial ? "text-[#b38f00]" : "text-[var(--term-muted)]"}`}
            data-testid="ic-coverage" title="Open Coverage for the per-chain report">
            range {cands.data.coverage.requested_below} below / {cands.data.coverage.requested_above} above ATM · {cands.data.coverage.chains_complete} chains complete
            {cands.data.coverage.chains_partial ? ` · ${cands.data.coverage.chains_partial} PARTIAL (${Object.keys(cands.data.coverage.partial_reasons).join(", ")})` : ""}
            {` · ${Number(cands.data.coverage.combinations).toLocaleString("en-IN")} combinations`}</div>}
          {icFilterText(status.data?.settings) && <div className="text-[11px] text-[#b38f00]" data-testid="ic-active-filters">
            Your saved filters narrow this list: {icFilterText(status.data?.settings)}. Change them in Scanner Settings if no candidates appear.</div>}
          {compared.length > 1 && (
            <div className="rounded-md border border-[var(--term-border)] p-2 overflow-auto" data-testid="ic-compare">
              <div className="text-[11px] font-bold mb-1">Comparing {compared.length}</div>
              <table className="text-xs mono"><tbody>
                {[["Underlying", (r) => `${r.underlying} ${r.expiry}`], ["Strikes", (r) => r.metrics.strikes.join(" / ")],
                  ["Net credit", (r) => unitOr(r, "net_credit")], ["Max loss", (r) => unitOr(r, "max_loss")],
                  ["Breakevens", (r) => `${be(r.metrics.breakeven_low)} – ${be(r.metrics.breakeven_high)}`],
                  ["BE distance (expiry)", (r) => `${beDistanceText(r.spot, r.metrics.breakeven_low, r.metrics.breakeven_high) || "no spot"}${r.stale ? " · stale spot" : ""}`],
                  ["Return on risk", (r) => pct(r.metrics.return_on_risk_pct)], ["Broker margin", (r) => marginText(r.capital)],
                  ["Return on margin", (r) => pct(r.capital?.return_on_margin_pct)], ["Quote age", (r) => age(r.quote_age_s)]].map(([k, f]) => (
                  <tr key={k}><th className="text-left pr-4 text-[var(--term-muted)] font-semibold">{k}</th>{compared.map((r) => <td key={r.id} className="pr-6">{f(r)}</td>)}</tr>))}
              </tbody></table>
            </div>)}
          <ScannerTable cols={cols} rows={rows} rowKey={(r) => r.id} ctx={{ compare, setCompare }} sort={sort} onSort={onSort}
            rowProps={(r) => ({ onClick: () => setOpen(r.id), className: `border-t border-[var(--term-border)] cursor-pointer hover:bg-[var(--term-s3)] ${r.stale ? "opacity-60" : ""}`,
              "data-testid": `ic-row-${r.id}` })}
            empty={running ? "No iron condor meets the settings yet. The sweep may still be visiting chains, or Settings may be too strict."
              : "The scanner is stopped. Press Start scanner to search every NFO and MCX option chain."}
            maxHeight="calc(100vh - 340px)" testId="ic-table" />
        </Section>}
      </>}

      {tab === "builder" && <Section title={kind === "BOX" ? "Box spread builder" : "Iron condor builder"}
        hint="Opened from a scanner row, the legs arrive pre-filled. Nothing is sent until you open the trade."
        right={<Segmented label="Builder strategy" testId="builder-kind" value={kind} onChange={setBuilderKind}
          options={[["IRON_CONDOR", "Iron Condor"], ["BOX", "Box Spread"]]} />} testId="builder-section">
        {kind === "IRON_CONDOR" && <IcBuilder seed={seed} running={running} settings={status.data?.settings}
          trading={IRON_CONDOR_TRADING_ENABLED} onCreated={() => { setTradesKind("IRON_CONDOR"); setTab("trades"); }} />}
        {kind === "BOX" && <BoxBuilder seed={boxSeed} running={running} trading={BOX_SPREAD_TRADING_ENABLED}
          onCreated={() => { setTradesKind("BOX"); setTab("trades"); }} />}
      </Section>}

      {tab === "trades" && !anyTrading && <Section testId="trades-off"><div className="py-8 text-center text-sm text-[var(--term-muted)]">
        Simulated trading for Box Spread and Iron Condor is not enabled in this build.</div></Section>}
      {tab === "trades" && anyTrading && <Section title={tKind === "BOX" ? "Box spread trades" : "Iron condor trades"}
        hint="Simulated trades only: marks, exits and risk limits."
        right={IRON_CONDOR_TRADING_ENABLED && BOX_SPREAD_TRADING_ENABLED && <Segmented label="Trades strategy" testId="trades-kind" value={tKind}
          onChange={setTradesKind} options={[["IRON_CONDOR", "Iron Condor"], ["BOX", "Box Spread"]]} />} testId="trades-section">
        {IRON_CONDOR_TRADING_ENABLED && tKind === "IRON_CONDOR" && <IcTrades />}
        {BOX_SPREAD_TRADING_ENABLED && tKind === "BOX" && <BoxTrades />}
      </Section>}
      <IcDetail id={open} onClose={() => setOpen(null)} onOpenBuilder={openBuilder} />
    </div>
  );
}
